import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, Search, ArrowLeft, LogOut, X, ChevronDown } from "lucide-react";
import { AdminAuthProvider, AdminLogin, useAdminAuth } from "../components/AdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ["New", "Reviewed", "Strong Candidate", "Contacted", "Not Available", "Do Not Use"];

const TalentAdminContent = () => {
  const { isAuthenticated, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    document.title = 'Talent Network Admin | Internal';
    return () => { if (metaRobots) metaRobots.content = 'index, follow'; };
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchSubmissions();
  }, [isAuthenticated]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/talent-network/submissions`, { headers: getAuthHeader() });
      setSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/talent-network/submissions/${id}/status`, { status }, { headers: { ...getAuthHeader(), "Content-Type": "application/json" } });
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      if (selected && selected.id === id) setSelected({ ...selected, status });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const downloadResume = (id, filename) => {
    const token = sessionStorage.getItem("admin_token");
    const url = `${API}/talent-network/resume/${id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename || "resume.pdf";
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";

  const filtered = submissions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.first_name, s.last_name, s.email, s.phone, s.city, s.state, s.primary_specialty, s.current_availability]
      .some(field => field && field.toLowerCase().includes(q));
  });

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-400">Loading...</p></div>;
  if (!isAuthenticated) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/blog" className="text-slate-400 hover:text-white text-sm flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" /> Blog Admin
            </Link>
            <h1 className="text-xl font-bold">Talent Network</h1>
            <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded text-xs">{submissions.length} total</span>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-white flex items-center text-sm">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, city, state, specialty..."
            className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            data-testid="talent-admin-search"
          />
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-slate-400 text-center py-10">Loading submissions...</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400 text-center py-10">No submissions found.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm" data-testid="talent-admin-table">
              <thead className="bg-slate-900 text-slate-400 text-left">
                <tr>
                  <th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Email</th>
                  <th className="px-3 py-3">Phone</th>
                  <th className="px-3 py-3">Location</th>
                  <th className="px-3 py-3">Specialty</th>
                  <th className="px-3 py-3">Availability</th>
                  <th className="px-3 py-3">Experience</th>
                  <th className="px-3 py-3">Travel</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Resume</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map(sub => (
                  <tr key={sub.id} className="hover:bg-slate-900/50 cursor-pointer" onClick={() => setSelected(sub)}>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-400">{formatDate(sub.created_at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap font-medium">{sub.first_name} {sub.last_name}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-300">{sub.email}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-300">{sub.phone}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-300">{sub.city}, {sub.state}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-300">{sub.primary_specialty}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-300">{sub.current_availability}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-400">{sub.years_of_experience || "-"}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-400">{sub.willing_to_travel || "-"}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <select
                        value={sub.status || "New"}
                        onChange={(e) => { e.stopPropagation(); updateStatus(sub.id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:outline-none"
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadResume(sub.id, sub.resume_filename); }}
                        className="text-blue-400 hover:text-blue-300 flex items-center text-xs"
                      >
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{selected.first_name} {selected.last_name}</h2>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone} />
              <Row label="Location" value={`${selected.city}, ${selected.state}`} />
              <Row label="Primary Specialty" value={selected.primary_specialty} />
              <Row label="Availability" value={selected.current_availability} />
              <Row label="Years of Experience" value={selected.years_of_experience} />
              <Row label="Willing to Travel" value={selected.willing_to_travel} />
              <Row label="States Willing to Work" value={selected.states_willing_to_work} />
              <Row label="Notes" value={selected.notes} />
              <Row label="Resume" value={selected.resume_filename} />
              <Row label="Status" value={selected.status || "New"} />
              <Row label="Submitted" value={formatDate(selected.created_at)} />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => downloadResume(selected.id, selected.resume_filename)}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm flex items-center"
              >
                <Download className="h-4 w-4 mr-2" /> Download Resume
              </button>
              <select
                value={selected.status || "New"}
                onChange={(e) => updateStatus(selected.id, e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex">
    <span className="text-slate-500 w-40 flex-shrink-0">{label}:</span>
    <span className="text-slate-200">{value || "-"}</span>
  </div>
);

const TalentAdmin = () => (
  <AdminAuthProvider>
    <TalentAdminContent />
  </AdminAuthProvider>
);

export default TalentAdmin;
